import { useEffect } from "react";
import { GuardrailResult, PhotoGuardrailError, RejectedPhoto } from "common/apis";
import { useI18N } from "common/hooks";
import { useCustomToast } from "./useCustomToast";

export type GuardrailImageFeedback = {
  /** Array positions of photos the guardrail rejected (maps to the local selection order). */
  rejectedInputIndexes: number[];
  /** HTTP 200 + some photos dropped — the action still succeeded (partial). */
  isPartial: boolean;
  /** HTTP 422 — the whole action was rejected, nothing saved. */
  isRejected: boolean;
  /** HTTP 503 — guardrail unavailable (fail-closed). */
  isUnavailable: boolean;
};

export type GuardrailFeedbackOptions = {
  /** Verb shown before the dropped-photo count on partial success, e.g. "Report saved". */
  savedLabel?: string;
};

/**
 * DEV-444 — surfaces a photo content-safety guardrail outcome for an upload mutation:
 * logs the (internal) rejection reasons, shows a generic localized message, and returns
 * the classification so the component can prune/keep photos and decide whether to close.
 *
 * Partial rejections arrive on `data` (`data.rejectedPhotos`); hard rejections / outages
 * arrive as a thrown `PhotoGuardrailError` in `error`.
 */
export function useGuardrailImageFeedback(
  data: GuardrailResult | null | undefined,
  error: unknown,
  options?: GuardrailFeedbackOptions,
): GuardrailImageFeedback {
  const showToast = useCustomToast();
  const i18n = useI18N();

  const partialRejections: RejectedPhoto[] = data?.rejectedPhotos ?? [];
  const guardErr = error instanceof PhotoGuardrailError ? error : null;
  const savedLabel = options?.savedLabel ?? i18n.get("photoGuardrailSavedDefault");

  // Partial success (HTTP 200 with some photos dropped) — one combined toast:
  // "<savedLabel> — N photo(s) wasn't attached" + a generic reason line.
  useEffect(() => {
    const count = partialRejections.length;
    if (!count) return;
    partialRejections.forEach((r) =>
      console.warn("[guardrail] photo dropped:", r.reason),
    );
    const dropped =
      count === 1
        ? i18n.get("photoGuardrailDroppedOne")
        : i18n.get("photoGuardrailDroppedMany").replace("{count}", String(count));
    showToast("", "warning", {
      title: `${savedLabel} — ${dropped}`,
      description:
        count === 1
          ? i18n.get("photoGuardrailReasonOne")
          : i18n.get("photoGuardrailReasonMany"),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Hard rejection (422) or unavailable (503).
  useEffect(() => {
    if (!guardErr) return;
    guardErr.rejectedPhotos.forEach((r) =>
      console.warn("[guardrail] photo rejected:", r.reason),
    );
    showToast(
      i18n.get(
        guardErr.kind === "unavailable"
          ? "photoGuardrailUnavailable"
          : guardErr.rejectedPhotos.length > 1
            ? "photoGuardrailAllRejectedMany"
            : "photoGuardrailAllRejectedOne",
      ),
      "error",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return {
    rejectedInputIndexes: (guardErr
      ? guardErr.rejectedPhotos
      : partialRejections
    ).map((r) => r.inputIndex),
    isPartial: !guardErr && partialRejections.length > 0,
    isRejected: guardErr?.kind === "rejected",
    isUnavailable: guardErr?.kind === "unavailable",
  };
}
